// Поиск «Избранного» (макет 1073:19438, HQ-1058): что рисовать по полю и ответу общего поиска. Ручка — та же, что у
// SearchScreen; из выдачи берём только блок вида текущей вкладки (teams / leagues / players), каталог не показываем.
import { searchPlayerSubtitle, searchTeamSubtitle, type SearchResponse } from '../../api/searchTypes';
import type { EmptyState } from '../../api/types';
import type { StringKey } from '../../i18n/i18n';

export type SearchKind = 'team' | 'league' | 'player';

/** Вид поиска по вкладке «Избранного»: Leagues / Players — свои, Teams и Matches — команды. */
export const searchKindOf = (tab: string | undefined): SearchKind => (tab === 'leagues' ? 'league' : tab === 'players' ? 'player' : 'team');

/** Заглушка «Find Your …» (1073:20924 · 1073:21770 · 1073:22487): заголовок и подпись по виду. */
export const START_TEXT: Record<SearchKind, { title: StringKey; text: StringKey }> = {
  team: { title: 'tg_fav_find_team', text: 'tg_fav_find_team_text' },
  league: { title: 'tg_fav_find_league', text: 'tg_fav_find_league_text' },
  player: { title: 'tg_fav_find_player', text: 'tg_fav_find_player_text' },
};

/** Строка выдачи (1073:23202): картинка, имя, подпись. */
export interface SearchHit { id: number; name: string; image: string | null; code?: string | null; subtitle: string }

/** Блок выдачи по запросу для вида — популярное каталога (popular_*) сюда не попадает. */
export function hitsOf(data: SearchResponse, kind: SearchKind): SearchHit[] {
  if (kind === 'team') return (data.teams ?? []).map((x) => ({ id: x.id, name: x.name, image: x.image, code: x.code, subtitle: searchTeamSubtitle(x) }));
  if (kind === 'league') return (data.leagues ?? []).map((x) => ({ id: x.id, name: x.name, image: x.image, code: x.code, subtitle: x.country ?? '' }));
  return (data.players ?? []).map((x) => ({ id: x.id, name: x.name, image: x.image, subtitle: searchPlayerSubtitle(x) }));
}

export type FavSearchView =
  | { kind: 'start' }
  | { kind: 'loading' }
  | { kind: 'error'; offline?: true }
  | { kind: 'empty'; state: EmptyState | undefined }
  | { kind: 'results'; hits: SearchHit[] };

/** Состояние режима поиска. text — поле как есть, q — устоявшийся (после паузы ввода) запрос, ушедший на сервер. */
export function favSearchView(text: string, q: string, query: { data?: SearchResponse; isPlaceholderData: boolean; isError: boolean; fetchStatus?: string },
  kind: SearchKind): FavSearchView {
  if (!text.trim()) return { kind: 'start' };
  const { data } = query;
  // ошибка набранного запроса — ошибка, а не вечный спиннер над прошлым ответом (keepPreviousData)
  if (query.isError && (!data || query.isPlaceholderData)) return { kind: 'error' };
  // без сети запрос на паузе (isError нет): ответа на набранное не будет — «Нет сети» с повтором, как QueryFallback
  if (query.fetchStatus === 'paused' && (!data || query.isPlaceholderData)) return { kind: 'error', offline: true };
  // спиннер вместо прошлой выдачи, пока ответа на набранное нет (набранное ещё ждёт паузы ввода или едет)
  if (!data || query.isPlaceholderData || text.trim() !== q) return { kind: 'loading' };
  // короче двух знаков сервер отдаёт каталог (заметка макета 1073:21338) — это ещё не поиск
  if (data.mode !== 'results') return { kind: 'start' };
  const hits = hitsOf(data, kind);
  return hits.length ? { kind: 'results', hits } : { kind: 'empty', state: data.empty_state };
}

/** Поиск «Избранного», сохранённый в state записи истории перед переходом на сущность: «Назад» возвращает его (HQ-1796). */
export interface SavedFavSearch { text: string; endpoint?: string }

/** Сохранённый поиск из location.state; чужое или пустое состояние — null (режим поиска закрыт). */
export function restoredFavSearch(state: unknown): SavedFavSearch | null {
  const saved = (state as { search?: unknown } | null)?.search as Partial<SavedFavSearch> | undefined;
  if (!saved || typeof saved !== 'object' || typeof saved.text !== 'string') return null;
  return typeof saved.endpoint === 'string' ? { text: saved.text, endpoint: saved.endpoint } : { text: saved.text };
}

function stateObject(state: unknown): Record<string, unknown> {
  return state && typeof state === 'object' ? { ...(state as Record<string, unknown>) } : {};
}

/** state текущей записи с сохранённым поиском; прочие ключи (в том числе tab) не трогаем. */
export function withFavSearch(state: unknown, text: string, endpoint: string | undefined): Record<string, unknown> {
  return { ...stateObject(state), search: endpoint === undefined ? { text } : { text, endpoint } };
}

/** state текущей записи без поиска («Cancel»): следующий возврат на запись поиск не откроет; прочие ключи — как были. */
export function withoutFavSearch(state: unknown): Record<string, unknown> {
  const { search: _search, ...rest } = stateObject(state);
  return rest;
}
